/**
 * PATH: packages/ui/src/components/NotificationCenter.tsx
 * FILE_ID: UI-NOTIFCENTER-SHELL-001
 * PURPOSE: Cross-hub notification panel. Dropdown trigger or fixed overlay.
 * WHY: The shell uses the app card surface. slate-900 read as a blue panel.
 */

import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell } from 'lucide-react'
import { cn } from '../lib/utils'

// Sub-modules
import { NotificationPanel } from './notification-center/NotificationPanel'
import { useNotifications } from './notification-center/useNotifications'
import type { NotificationCenterProps, FilterType } from './notification-center/types'

// Re-export for public API
export type { NotificationItem, NotificationCenterProps } from './notification-center/types'
export { useNotifications }

export function NotificationCenter({
  notifications: propNotifications,
  unreadCount: propUnreadCount = 0,
  isLoading = false,
  isOpen: controlledOpen,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onDismissAll,
  onOpenSettings,
  onNavigate,
  hideTrigger = false,
}: NotificationCenterProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = controlledOpen ?? internalOpen
  const setIsOpen = onClose ? () => onClose() : setInternalOpen
  const [filter, setFilter] = useState<FilterType>('focus')
  const panelRef = useRef<HTMLDivElement>(null)

  const notifications = propNotifications ?? []
  const unreadCount = propUnreadCount ?? notifications.filter((n) => !n.read_at).length

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Fixed overlay mode (trigger is in Header)
  if (hideTrigger) {
    if (!isOpen) return null

    return (
      <>
        <div className="fixed inset-0 z-[100] bg-black/20" onClick={() => onClose?.()} />
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.2 }}
          className="fixed top-16 right-4 w-[min(420px,calc(100vw-2rem))] max-h-[calc(100vh-5rem)] bg-card text-foreground rounded-2xl shadow-2xl border border-border overflow-hidden z-[101] flex flex-col"
        >
          <NotificationPanel
            notifications={notifications}
            unreadCount={unreadCount}
            isLoading={isLoading}
            filter={filter}
            onFilterChange={setFilter}
            onMarkAsRead={onMarkAsRead}
            onMarkAllAsRead={onMarkAllAsRead}
            onDismiss={onDismiss}
            onDismissAll={onDismissAll}
            onOpenSettings={onOpenSettings}
            onNavigate={(url) => { onClose?.(); onNavigate?.(url); }}
            onClose={onClose}
            showCloseButton
            listMaxHeight="flex-1"
          />
        </motion.div>
      </>
    )
  }

  // Dropdown trigger mode
  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative p-2 rounded-xl transition-colors',
          isOpen ? 'bg-muted' : 'hover:bg-muted'
        )}
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-[min(420px,calc(100vw-2rem))] max-h-[600px] bg-card text-foreground rounded-2xl shadow-2xl border border-border overflow-hidden z-[101]"
          >
            <NotificationPanel
              notifications={notifications}
              unreadCount={unreadCount}
              isLoading={isLoading}
              filter={filter}
              onFilterChange={setFilter}
              onMarkAsRead={onMarkAsRead}
              onMarkAllAsRead={onMarkAllAsRead}
              onDismiss={onDismiss}
              onDismissAll={onDismissAll}
              onOpenSettings={onOpenSettings}
              onNavigate={(url) => { setInternalOpen(false); onNavigate?.(url); }}
              listMaxHeight="max-h-[450px]"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationCenter
